import React from 'react';
import ReactDOM from 'react-dom';
import { act, Simulate } from 'react-dom/test-utils';

// react-datepicker は jsdom 上でのポップアップ描画が複雑なため、このテストでは
// 「別日を選択」ボタン自体のクリック挙動（開閉）は対象外とし、モーダルへの移設と
// loadSearchResult の呼び出しパラメータが変わっていないことの検証に絞る。
jest.mock('react-datepicker', () => {
  const React = require('react');
  return {
    __esModule: true,
    default: ({ customInput }) => React.cloneElement(customInput, { onClick: () => {} }),
    registerLocale: () => {},
  };
});

// eslint-disable-next-line import/first
import SearchForm from './search_form';

describe('SearchForm（TGW-2619 検索カレンダーUI刷新）', () => {
  let container;
  let loadSearchResult;

  const renderForm = (props = {}) => {
    loadSearchResult = jest.fn();
    container = document.createElement('div');
    document.body.appendChild(container);
    act(() => {
      ReactDOM.render(
        <SearchForm
          keyword=""
          loadSearchResult={loadSearchResult}
          eventCondition=""
          eventDate={null}
          timeSlot={null}
          companyId={null}
          {...props}
        />,
        container
      );
    });
  };

  afterEach(() => {
    ReactDOM.unmountComponentAtNode(container);
    container.remove();
  });

  it('画面右端の並べ替えショートカットは押すたびに 人気順→開催順→新着→人気順 の順に切り替わる', () => {
    renderForm();
    const shortcutButton = container.querySelector('.search-event__popular-link-2026');
    expect(shortcutButton).not.toBeNull();
    expect(shortcutButton.textContent).toContain('人気順');

    // 1回目のクリック: 人気順 → 開催順
    act(() => {
      shortcutButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });
    expect(loadSearchResult).toHaveBeenLastCalledWith({
      keyword: '',
      circleOrder: '',
      eventCondition: 'recently',
      eventDate: null,
    });
  });

  it('「別条件で検索する」ボタンでモーダルが開閉する', () => {
    renderForm();
    expect(container.querySelector('.modal-view')).toBeNull();

    const openButton = container.querySelector('.search-event__more-conditions-btn-2026');
    act(() => {
      openButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });
    expect(container.querySelector('.modal-view')).not.toBeNull();

    const closeButton = container.querySelector('.modal-xBtn');
    act(() => {
      closeButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });
    expect(container.querySelector('.modal-view')).toBeNull();
  });

  it('モーダル内のキーワード検索欄で Enter を押すと、従来と同じ形で loadSearchResult が呼ばれる', () => {
    renderForm();
    act(() => {
      container
        .querySelector('.search-event__more-conditions-btn-2026')
        .dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });

    const input = container.querySelector('.modal-view input.m-search-textbox');
    expect(input).not.toBeNull();

    act(() => {
      const setter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      ).set;
      setter.call(input, '飲み会');
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    act(() => {
      // KeyboardEventのkeyCodeはコンストラクタのinit経由での設定がjsdomのバージョンによって
      // 効かないことがあるため、確実に反映されるよう明示的に上書きする。
      const keyDownEvent = new KeyboardEvent('keydown', { bubbles: true });
      Object.defineProperty(keyDownEvent, 'keyCode', { value: 13 });
      input.dispatchEvent(keyDownEvent);
    });

    expect(loadSearchResult).toHaveBeenCalledWith({ keyword: '飲み会' });
  });

  it('モーダル内の「開催順」ボタンで、従来と同じ形で loadSearchResult が呼ばれる', () => {
    renderForm();
    act(() => {
      container
        .querySelector('.search-event__more-conditions-btn-2026')
        .dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });

    const recentlyButton = Array.from(
      container.querySelectorAll('.modal-view button')
    ).find((el) => el.textContent === '開催順');
    expect(recentlyButton).not.toBeUndefined();

    act(() => {
      recentlyButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });

    expect(loadSearchResult).toHaveBeenCalledWith({
      keyword: '',
      circleOrder: '',
      eventCondition: 'recently',
      eventDate: null,
    });
  });

  it('モーダル内にも「人気順」ボタンがあり、押すと画面上のショートカットと同じ形で loadSearchResult が呼ばれる', () => {
    renderForm();
    act(() => {
      container
        .querySelector('.search-event__more-conditions-btn-2026')
        .dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });

    const popularButtonInModal = Array.from(
      container.querySelectorAll('.modal-view button')
    ).find((el) => el.textContent === '人気順');
    expect(popularButtonInModal).not.toBeUndefined();

    act(() => {
      popularButtonInModal.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });

    expect(loadSearchResult).toHaveBeenCalledWith({
      keyword: '',
      circleOrder: '',
      eventCondition: '',
      eventDate: null,
    });
  });

  it('新着が選択されている（eventCondition=new）とき、表示は「新着」になり、押すと人気順に切り替わる', () => {
    renderForm({ eventCondition: 'new' });

    const shortcutButton = container.querySelector('.search-event__popular-link-2026');
    expect(shortcutButton).not.toBeNull();
    expect(shortcutButton.textContent).toContain('新着');

    act(() => {
      shortcutButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });

    // 新着 → 人気順 のサイクル
    expect(loadSearchResult).toHaveBeenCalledWith({
      keyword: '',
      circleOrder: '',
      eventCondition: '',
      eventDate: null,
    });
  });

  it('開催順が選択されている（eventCondition=recently）とき、表示は「開催順」になり、押すと新着に切り替わる', () => {
    renderForm({ eventCondition: 'recently' });

    const shortcutButton = container.querySelector('.search-event__popular-link-2026');
    expect(shortcutButton).not.toBeNull();
    expect(shortcutButton.textContent).toContain('開催順');

    act(() => {
      shortcutButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });

    // 開催順 → 新着 のサイクル
    expect(loadSearchResult).toHaveBeenCalledWith({
      keyword: '',
      circleOrder: '',
      eventCondition: 'new',
      eventDate: null,
    });
  });

  it('モーダル内の時間帯フィルタ（朝）で、従来と同じ形で loadSearchResult が呼ばれる', () => {
    renderForm();
    act(() => {
      container
        .querySelector('.search-event__more-conditions-btn-2026')
        .dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });

    const morningButton = Array.from(
      container.querySelectorAll('.modal-view button')
    ).find((el) => el.textContent === '朝');
    expect(morningButton).not.toBeUndefined();

    act(() => {
      morningButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });

    expect(loadSearchResult).toHaveBeenCalledWith({
      keyword: '',
      circleOrder: '',
      eventCondition: '',
      eventDate: null,
      timeSlot: 'morning',
    });
  });

  it('細かく設定の終わりは 24:00 まで選べ、選んだ値で loadSearchResult が呼ばれる（TGW-2817）', () => {
    renderForm({ timeSlot: 'custom' });
    act(() => {
      container
        .querySelector('.search-event__more-conditions-btn-2026')
        .dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });

    const [fromSelect, toSelect] = container.querySelectorAll('.modal-view select');
    const labels = (select) => Array.from(select.options).map((o) => o.textContent);
    expect(labels(toSelect)[labels(toSelect).length - 1]).toBe('24:00');
    expect(labels(fromSelect)).not.toContain('24:00');

    act(() => {
      toSelect.value = '24';
      Simulate.change(toSelect);
    });

    expect(loadSearchResult).toHaveBeenLastCalledWith({
      keyword: '',
      circleOrder: '',
      eventCondition: '',
      eventDate: null,
      timeSlot: 'custom',
      timeSlotFrom: 9,
      timeSlotTo: 24,
    });
  });

  it('eventCondition が circle のときは日付・人気順は表示されないが、モーダルのキーワード検索は残る', () => {
    renderForm({ eventCondition: 'circle' });

    expect(container.querySelector('.search-event__popular-link-2026')).toBeNull();
    expect(container.querySelector('.search-event__date-wrap')).toBeNull();

    const openButton = container.querySelector('.search-event__more-conditions-btn-2026');
    expect(openButton).not.toBeNull();

    act(() => {
      openButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });

    expect(container.querySelector('.modal-view input.m-search-textbox')).not.toBeNull();
    // circle検索モードでは開催順・時間帯フィルタは元々対象外（従来通り非表示）
    expect(container.querySelector('.modal-view .search-event__narrow-btn')).toBeNull();
  });
});
